import { useEffect } from 'react'
import type { SessionCommand } from '@fitting-room/contracts'
import type { FlowContext } from './types'

/**
 * Declare what this screen would send if she left it by a door that is not
 * Continue.
 *
 * A questionnaire screen commits on Continue, which is right while forwards is
 * the only way out. Editing an answer from the deck opens a second way out —
 * "Back to browsing" — and every screen that held its answer in local state
 * dropped it there: she swapped a silhouette, went back to the deck, and got
 * the deck she already had, with nothing on screen to say her change had gone.
 *
 * `build` returning `null` means "nothing to carry", and that is the case a
 * screen must be careful about: a suggestion she has not touched, or a contact
 * field prefilled from the booking, must not be written down as HER answer
 * just because she walked back out through the deck. Only what she changed
 * travels.
 */
export function usePendingExit(ctx: FlowContext, build: (() => readonly SessionCommand[]) | null): void {
  const set = ctx.setPendingExit

  // No dependency list, on purpose. What this writes changes with every tap
  // and every keystroke, and a list here would be a hand-kept copy of
  // everything the step renders from — the kind that goes stale silently.
  // Writing a ref is cheap; being one render behind is not.
  useEffect(() => {
    set(build ? build() : [])
  })

  // And gone when the screen is. An unsent answer belongs to the screen that
  // was showing it, never to the next one.
  useEffect(() => () => set([]), [set])
}
